import {
  ChangeDetectionStrategy,
  Component,
  ChangeDetectorRef,
  ElementRef,
  EventEmitter,
  Input,
  AfterViewInit,
  OnDestroy,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ProximityAlertMessage, SimulationSocketService, AdvocateLogMessage } from 'src/app/services';
import type { SimulationActivityType } from 'src/app/utils';

export { AdvocateLogMessage };

export type ProximitySimulationOverlayZone = 'red' | 'yellow' | 'outside' | 'unknown';
export type { SimulationActivityType };

@Component({
  selector: 'app-proximity-simulation-overlay',
  templateUrl: './proximity-simulation-overlay.component.html',
  styleUrls: ['./proximity-simulation-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProximitySimulationOverlayComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() distanceParameter = 'meter';
  @Input() isPerpetrator = false;
  @Input() threatPairId = '';
  @Input() title = 'Simulation mode';
  @Input() zone: ProximitySimulationOverlayZone = 'unknown';
  @Input() completedCheckIns = 0;
  @Input() totalCheckIns = 0;
  @Input() survivorName = 'Survivor';
  @Input() perpetratorName = 'Perpetrator';
  @Input() telemetryExpanded = false;
  @Input() socketUserIds: string[] = [];
  @Input() isDraggableMode = false;
  @Input() selectedActivity: SimulationActivityType = 'UNKNOWN';
  /** Per-party CHECK_IN_TIME from simulation check-in responses, in seconds. */
  @Input() survivorCheckInTimeSeconds: number | null = null;
  @Input() perpetratorCheckInTimeSeconds: number | null = null;
  /** When true, simulation runs offline without connecting to live WebSockets. */
  @Input() isLocalMode = false;
  @Input() localAlert?: {
    watch: 'survivor' | 'perpetrator' | 'both';
    survivorAlert?: ProximityAlertMessage;
    perpetratorAlert?: ProximityAlertMessage;
    alert?: ProximityAlertMessage;
  } | null = null;
  @Input() localAdvocateLog?: AdvocateLogMessage | null = null;

  @Output() editSimulation = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();
  @Output() restart = new EventEmitter<void>();
  @Output() activityChange = new EventEmitter<SimulationActivityType>();

  readonly activityOptions: SimulationActivityType[] = [
    'UNKNOWN',
    'WALKING',
    'RUNNING',
    'ON_BICYCLE',
    'IN_VEHICLE',
    'STILL',
  ];

  @ViewChild('eventLogList') private eventLogList?: ElementRef<HTMLOListElement>;
  eventLogs: AdvocateLogMessage[] = [];

  now = new Date();
  private survivorAlert: ProximityAlertMessage | undefined;
  private perpetratorAlert: ProximityAlertMessage | undefined;
  private readonly subscriptions = new Subscription();
  private survivorAlertTimer: ReturnType<typeof setTimeout> | undefined;
  private perpetratorAlertTimer: ReturnType<typeof setTimeout> | undefined;
  private survivorRealertTimer: ReturnType<typeof setTimeout> | undefined;
  private perpetratorRealertTimer: ReturnType<typeof setTimeout> | undefined;
  private logScrollHandle: ReturnType<typeof setTimeout> | undefined;
  private isSurvivorAlertDismissed = false;
  private isPerpetratorAlertDismissed = false;
  private clockHandle = setInterval(() => {
    this.now = new Date();
    this.changeDetector.markForCheck();
  }, 1000);

  constructor(
    private simulationSocketService: SimulationSocketService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    if (this.isLocalMode) {
      return;
    }

    const [survivorUserId, perpetratorUserId] = this.socketUserIds;

    const onProximityAlert = (userId: string, alert: ProximityAlertMessage): void => {
      if (userId === survivorUserId) {
        this.survivorAlert = alert;
        this.isSurvivorAlertDismissed = false;
        this.startAlertTimer('survivor');
      } else if (userId === perpetratorUserId) {
        this.perpetratorAlert = alert;
        this.isPerpetratorAlertDismissed = false;
        this.startAlertTimer('perpetrator');
      }
      this.changeDetector.markForCheck();
    };

    this.simulationSocketService.listenForProximityAlerts('user-id-1').subscribe((alert) => {
    });

    if (survivorUserId) {
      this.subscriptions.add(
        this.simulationSocketService.listenForProximityAlerts(survivorUserId).subscribe((alert) => {
          onProximityAlert(survivorUserId, alert);
        }),
      );
    }

    if (perpetratorUserId && perpetratorUserId !== survivorUserId) {
      this.subscriptions.add(
        this.simulationSocketService.listenForProximityAlerts(perpetratorUserId).subscribe((alert) => {
          onProximityAlert(perpetratorUserId, alert);
        }),
      );
    }

    this.subscriptions.add(
      this.simulationSocketService.listenForAdvocateLogs(this.threatPairId, this.distanceParameter).subscribe((log) => {
        this.eventLogs.unshift(log);
        //this.eventLog.splice(20);
        this.changeDetector.markForCheck();
        this.scrollEventLogToLatest();
      }),
    );
  }

  get isCompleted(): boolean {
    return this.totalCheckIns > 0 && this.completedCheckIns >= this.totalCheckIns;
  }

  get isSurvivorAlerting(): boolean {
    return !this.isSurvivorAlertDismissed && this.survivorAlertBody.length > 0;
  }

  get isPerpetratorAlerting(): boolean {
    return !this.isPerpetratorAlertDismissed && this.perpetratorAlertBody.length > 0;
  }

  private isBreachAlert(alert?: ProximityAlertMessage): boolean {
    if (!alert || !alert.body) {
      return false;
    }
    return alert.body !== 'Notice: All Clear';
  }

  ngOnChanges(changes: Record<string, { currentValue: unknown; previousValue?: unknown }>): void {
    if (
      (changes['completedCheckIns']?.currentValue === 0 ||
        changes['completedCheckIns']?.currentValue === 1) &&
      this.isLocalMode
    ) {
      this.eventLogs = [];
      this.survivorAlert = undefined;
      this.perpetratorAlert = undefined;
      this.isSurvivorAlertDismissed = false;
      this.isPerpetratorAlertDismissed = false;
      this.clearAlertTimer();
    }

    if (this.isLocalMode && changes['localAlert']) {
      if (this.localAlert) {
        const payload = this.localAlert;
        const newSurvivorAlert = payload.survivorAlert ?? payload.alert;
        const newPerpetratorAlert = payload.perpetratorAlert ?? payload.alert;

        if (payload.watch === 'survivor' || payload.watch === 'both') {
          const isNewAlert =
            !this.survivorAlert ||
            this.survivorAlert.body !== newSurvivorAlert?.body ||
            this.survivorAlert.sendAt !== newSurvivorAlert?.sendAt ||
            this.isSurvivorAlertDismissed;
          if (isNewAlert && newSurvivorAlert) {
            this.survivorAlert = newSurvivorAlert;
            this.isSurvivorAlertDismissed = false;
            this.startAlertTimer('survivor');
          }
        } else if (payload.watch === 'perpetrator') {
          this.survivorAlert = undefined;
          this.isSurvivorAlertDismissed = true;
          this.clearAlertTimer('survivor');
        }

        if (payload.watch === 'perpetrator' || payload.watch === 'both') {
          const isNewAlert =
            !this.perpetratorAlert ||
            this.perpetratorAlert.body !== newPerpetratorAlert?.body ||
            this.perpetratorAlert.sendAt !== newPerpetratorAlert?.sendAt ||
            this.isPerpetratorAlertDismissed;
          if (isNewAlert && newPerpetratorAlert) {
            this.perpetratorAlert = newPerpetratorAlert;
            this.isPerpetratorAlertDismissed = false;
            this.startAlertTimer('perpetrator');
          }
        } else if (payload.watch === 'survivor') {
          this.perpetratorAlert = undefined;
          this.isPerpetratorAlertDismissed = true;
          this.clearAlertTimer('perpetrator');
        }
        this.changeDetector.markForCheck();
      } else {
        this.perpetratorAlert = undefined;
        this.isPerpetratorAlertDismissed = true;
        this.clearAlertTimer('perpetrator');
        this.survivorAlert = undefined;
        this.isSurvivorAlertDismissed = true;
        this.clearAlertTimer('survivor');
        this.changeDetector.markForCheck();
      }
    }

    if (this.isLocalMode && changes['localAdvocateLog'] && this.localAdvocateLog) {
      this.eventLogs.unshift(this.localAdvocateLog);
      this.changeDetector.markForCheck();
      this.scrollEventLogToLatest();
    }

    if (changes['eventLog']) {
      this.scrollEventLogToLatest();
    }
    if (changes['completedCheckIns'] || changes['isDraggableMode']) {
      this.changeDetector.markForCheck();
    }
  }

  ngAfterViewInit(): void {
    this.scrollEventLogToLatest();
  }

  private scrollEventLogToLatest(): void {
    if (this.logScrollHandle) {
      clearTimeout(this.logScrollHandle);
    }
    this.logScrollHandle = setTimeout(() => {
      if (this.eventLogList) {
        this.eventLogList.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
      }
      this.logScrollHandle = undefined;
    });
  }

  dismissAlert(watch: 'survivor' | 'perpetrator'): void {
    if (watch === 'survivor') {
      this.isSurvivorAlertDismissed = true;
      this.clearAlertTimer('survivor');
      if (!this.isLocalMode) {
        this.survivorAlert = undefined;
      }
    } else {
      this.isPerpetratorAlertDismissed = true;
      this.clearAlertTimer('perpetrator');
      if (!this.isLocalMode) {
        this.perpetratorAlert = undefined;
      }
    }
    this.changeDetector.markForCheck();
  }

  private startAlertTimer(watch: 'survivor' | 'perpetrator'): void {
    this.clearAlertTimer(watch);
    const durationMs = this.isLocalMode ? 5000 : 8000;
    const timer = setTimeout(() => {
      if (watch === 'survivor') {
        this.isSurvivorAlertDismissed = true;
        if (!this.isLocalMode) {
          this.survivorAlert = undefined;
        }
      } else {
        this.isPerpetratorAlertDismissed = true;
        if (!this.isLocalMode) {
          this.perpetratorAlert = undefined;
        }
      }
      this.changeDetector.markForCheck();
    }, durationMs);
    if (watch === 'survivor') {
      this.survivorAlertTimer = timer;
    } else {
      this.perpetratorAlertTimer = timer;
    }
  }

  private scheduleRealertIfNeeded(_watch: 'survivor' | 'perpetrator'): void {
    // No-op: Alerts show once per check-in / alert event and do not loop endlessly
  }

  private clearAlertTimer(watch?: 'survivor' | 'perpetrator'): void {
    if (!watch || watch === 'survivor') {
      if (this.survivorAlertTimer) {
        clearTimeout(this.survivorAlertTimer);
        this.survivorAlertTimer = undefined;
      }
      if (this.survivorRealertTimer) {
        clearTimeout(this.survivorRealertTimer);
        this.survivorRealertTimer = undefined;
      }
    }
    if (!watch || watch === 'perpetrator') {
      if (this.perpetratorAlertTimer) {
        clearTimeout(this.perpetratorAlertTimer);
        this.perpetratorAlertTimer = undefined;
      }
      if (this.perpetratorRealertTimer) {
        clearTimeout(this.perpetratorRealertTimer);
        this.perpetratorRealertTimer = undefined;
      }
    }
  }

  get survivorAlertTitle(): string {
    return this.survivorAlert?.title || 'Proximity Alert';
  }

  get perpetratorAlertTitle(): string {
    return this.perpetratorAlert?.title || 'Proximity Alert';
  }

  get survivorAlertBody(): string {
    // Prefer this role's socket body; otherwise use the role-specific fallback
    // (not the other party's body) so the two watches never mirror each other.
    return this.survivorAlert?.body ? this.survivorAlert.body : '';
  }

  get perpetratorAlertBody(): string {
    return this.perpetratorAlert?.body ? this.perpetratorAlert.body : '';
  }

  get logCount(): number {
    return this.eventLogs.length;
  }

  get progressPercent(): number {
    if (!this.totalCheckIns) {
      return 0;
    }
    return Math.min((this.completedCheckIns / this.totalCheckIns) * 100, 100);
  }

  onActivitySelected(value: SimulationActivityType): void {
    this.selectedActivity = value;
    this.activityChange.emit(value);
    this.changeDetector.markForCheck();
  }

  formatCheckInTime(seconds: number | null): string {
    if (seconds == null || !Number.isFinite(seconds)) {
      return '—';
    }
    return `${Math.round(seconds)}s`;
  }

  trackLog(index: number): number {
    return index;
  }

  logBadgeClass(entry: AdvocateLogMessage): string {
    const value = this.logBadgeLabel(entry).toLowerCase();
    if (value.includes('red') || value.includes('emergency')) {
      return 'proximity-overlay__badge--emergency';
    }
    if (value.includes('yellow') || value.includes('warning')) {
      return 'proximity-overlay__badge--warning';
    }
    if (value.includes('cleared') || value.includes('clear') || value.includes('safe')) {
      return 'proximity-overlay__badge--cleared';
    }
    if (value.includes('buffer')) {
      return 'proximity-overlay__badge--buffer';
    }
    return 'proximity-overlay__badge--info';
  }

  logBadgeLabel(entry: AdvocateLogMessage): string {
    return entry.zone;
  }

  ngOnDestroy(): void {
    clearInterval(this.clockHandle);
    this.clearAlertTimer();
    if (this.logScrollHandle) {
      clearTimeout(this.logScrollHandle);
    }
    this.subscriptions.unsubscribe();
  }
}
